import { useState, useMemo } from 'react'

import { useOptimizedRecord } from '@/shared/hooks/useOptimizedQuery'
import { useSession } from '@/shared/hooks/useSession'
import { api } from '@/shared/services/api'
import { User } from '@/shared/types/user'


interface UseUserProfileProps {
  userUid?: string
  me?: boolean
}

export const useUserProfile = ({
  userUid: _userUid,
  me
}: UseUserProfileProps) => {
  const { data: session } = useSession()
  const userUid = me ? session?.user?.uid : _userUid

  const [isLoading, setIsLoading] = useState(false)

  const { record: userProfileRaw, refetch } = useOptimizedRecord<User>({
    path: `/user/profiles/${userUid}`,
    enabled: !!userUid
  })

  const toggleFollowUser = async ({ userUid }: { userUid: string }) => {
    if (isLoading) return
    setIsLoading(true)
    try {
      await api.post('/user/follows/toggle', {
        record: {
          followed_uid: userUid
        }
      })
      refetch()
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const userProfile = useMemo(() => {
    if (!userProfileRaw) return null

    const title =
      userProfileRaw.title ??
      (userProfileRaw.titles ?? []).sort((a, b) => b.points - a.points)[0]

    return {
      ...userProfileRaw,
      title
    }
  }, [userProfileRaw])

  return {
    userProfile,
    toggleFollowUser,
    refetchUserProfile: refetch,
    isLoading
  }
}
